import { useEffect, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { isAxiosError } from 'axios'
import QRCode from 'qrcode'
import { KeyRound, ShieldAlert } from 'lucide-react'

import { useMutation } from '@tanstack/react-query'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

import { api } from '@/lib/api/client'
import { useAuthStore } from '@/lib/auth/store'

interface StartRequest {
  password: string
}
interface StartResponse {
  secret: string
  otpauthUri: string
}
interface ConfirmRequest {
  password: string
  code: string
}
interface ConfirmResponse {
  ok: boolean
  error?: string | null
  accessToken?: string | null
  refreshToken?: string | null
  /** An admin-forced reset is still owed: the new tokens can only change the password. */
  passwordResetRequired?: boolean
}

/**
 * Enrol-during-login. Reached when {@code /v1/auth/login} returned
 * {@code totpPending: true} — credentials were valid but TOTP must be
 * enrolled before the session is fully authenticated. The current access
 * token is a short-lived, scope-limited interim token (see
 * {@code JwtAuthenticationFilter}'s TOTP allowlist) that can only reach
 * {@code /v1/auth/totp/*} and {@code /v1/auth/me} — this page is the only
 * thing it's good for.
 *
 * <p>Always a first-time enrolment (no rebind branch): totpPending only
 * fires when the user has no secret enrolled yet. On a successful confirm,
 * the backend upgrades the interim token to full access + refresh tokens
 * right here — no separate "verify again" round trip.</p>
 */
export function TotpSetupPage() {
  const navigate = useNavigate()
  const setSession = useAuthStore((s) => s.setSession)
  const logout = useAuthStore((s) => s.logout)
  const tenantSlug = useAuthStore((s) => s.tenantSlug)
  const empId = useAuthStore((s) => s.empId)

  const [password, setPassword] = useState('')

  const start = useMutation({
    mutationFn: async (body: StartRequest) =>
      (await api.post<StartResponse>('/v1/auth/totp/start', body)).data,
  })

  const confirm = useMutation({
    mutationFn: async (body: ConfirmRequest) =>
      (await api.post<ConfirmResponse>('/v1/auth/totp/confirm', body)).data,
    onSuccess: (data) => {
      if (data.ok && data.accessToken && data.refreshToken) {
        setSession({
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          tenantSlug: tenantSlug ?? '',
          empId: empId ?? '',
        })
        navigate({ to: data.passwordResetRequired ? '/auth/change-password' : '/' })
      }
    },
  })

  function onSignOut() {
    logout('user_initiated')
    navigate({ to: '/login' })
  }

  function onStartSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!password) return
    start.mutate({ password })
  }

  const startErrorMessage = (() => {
    if (!start.isError) return null
    const code = apiErrorCode(start.error)
    return code ? (ERROR_MESSAGES[code] ?? 'Could not start enrolment. Try again.')
      : 'Could not start enrolment. Try again.'
  })()

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-4">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-warning" />
            <CardTitle>Set up your authenticator</CardTitle>
          </div>
          <CardDescription>
            Your admin requires TOTP for this account. Pair an authenticator
            app (Google Authenticator, Authy, 1Password) to finish signing in.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!start.isSuccess && (
            <form onSubmit={onStartSubmit} className="space-y-3">
              <div>
                <Label htmlFor="setup-password">Re-enter your password</Label>
                <Input
                  id="setup-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  autoFocus
                  required
                />
              </div>
              {startErrorMessage && (
                <Alert variant="destructive">
                  <AlertTitle>Couldn't start enrolment</AlertTitle>
                  <AlertDescription>{startErrorMessage}</AlertDescription>
                </Alert>
              )}
              <Button type="submit" className="w-full" disabled={!password || start.isPending}>
                <KeyRound className="h-3.5 w-3.5" />
                {start.isPending ? 'Starting…' : 'Continue'}
              </Button>
            </form>
          )}

          {start.isSuccess && start.data && (
            <EnrolPanel
              secret={start.data.secret}
              otpauthUri={start.data.otpauthUri}
              isPending={confirm.isPending}
              error={confirmError(confirm)}
              onSubmit={(code) => confirm.mutate({ password, code })}
            />
          )}

          <Button variant="ghost" size="sm" className="w-full" onClick={onSignOut}>
            Sign out
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}

function EnrolPanel({
  secret,
  otpauthUri,
  isPending,
  error,
  onSubmit,
}: {
  secret: string
  otpauthUri: string
  isPending: boolean
  error: string | null
  onSubmit: (code: string) => void
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [code, setCode] = useState('')

  useEffect(() => {
    if (!canvasRef.current) return
    QRCode.toCanvas(canvasRef.current, otpauthUri, {
      width: 200,
      margin: 1,
      color: { dark: '#0E2138', light: '#FFFFFF' },
    }).catch(() => { /* swallow — UI degrades to the secret string */ })
  }, [otpauthUri])

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (code.length === 6) onSubmit(code)
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-[200px_1fr] sm:items-start">
        <div className="rounded-md border border-slate-200 bg-white p-3">
          <canvas ref={canvasRef} className="block" aria-label="TOTP enrolment QR code" />
        </div>
        <div className="space-y-3">
          <div>
            <Label>Or type the secret manually</Label>
            <Input value={secret} readOnly className="font-mono text-xs" />
          </div>
          <Alert variant="warning">
            <AlertTitle>Don't close this page yet</AlertTitle>
            <AlertDescription>
              You won't be signed in until you confirm a code below.
            </AlertDescription>
          </Alert>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="flex items-end gap-2">
        <div className="flex-1">
          <Label htmlFor="totp-setup-code">Confirmation code</Label>
          <Input
            id="totp-setup-code"
            inputMode="numeric"
            pattern="\d{6}"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            placeholder="123456"
            className="font-mono tracking-[0.4em]"
            autoFocus
          />
        </div>
        <Button type="submit" disabled={code.length !== 6 || isPending}>
          {isPending ? 'Confirming…' : 'Confirm & sign in'}
        </Button>
      </form>
      {error && (
        <Alert variant="destructive">
          <AlertTitle>Code rejected</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
    </div>
  )
}

const ERROR_MESSAGES: Record<string, string> = {
  password_required: 'Enter your account password.',
  password_invalid: 'Password incorrect.',
  code_invalid: 'Code incorrect. Try again — your phone clock may need to sync.',
}

function apiErrorCode(err: unknown): string | null {
  if (!isAxiosError(err)) return null
  const data = err.response?.data as { error?: string } | undefined
  return typeof data?.error === 'string' ? data.error : null
}

function confirmError(confirm: {
  isError: boolean
  error: unknown
  data: ConfirmResponse | undefined
}): string | null {
  if (confirm.isError) {
    const code = apiErrorCode(confirm.error)
    return code ? (ERROR_MESSAGES[code] ?? 'Could not confirm. Try again.') : 'Could not confirm. Try again.'
  }
  if (confirm.data && confirm.data.ok === false) {
    const code = confirm.data.error
    if (code && code in ERROR_MESSAGES) return ERROR_MESSAGES[code] ?? 'Code incorrect.'
    return code ?? 'Code incorrect.'
  }
  return null
}
